import { Link } from "react-router-dom";
import { Anchor, ArrowRight, Users, Bed, Wifi, LifeBuoy } from "lucide-react";
import { EXPERIENCES, IMAGES } from "../lib/data";
import MemberSpotlight from "../components/MemberSpotlight";
import InstagramStrip from "../components/InstagramStrip";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] w-full overflow-hidden">
        <img
          src={IMAGES.hero}
          alt="Aboard the Bavaria 35 — Anchor Management, Bermuda"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-40 pb-24 min-h-[92vh] flex flex-col justify-end text-white">
          <div className="max-w-3xl">
            <div className="overline text-white/80 mb-6 fade-up">Bermuda · Private Charter</div>
            <h1
              className="font-serif-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] fade-up fade-up-delay-1"
              data-testid="hero-heading"
            >
              The island, <em className="italic">from the water.</em>
            </h1>
            <p className="mt-8 text-base lg:text-lg text-white/85 max-w-xl leading-relaxed fade-up fade-up-delay-2">
              Bermuda's turquoise waters, pink-sand shores, and golden hour are
              best met from the deck of our privately-chartered 35ft Bavaria
              sailboat. Day cruises, sunset pours, corporate afternoons and
              overnight escapes — for up to ten guests.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 fade-up fade-up-delay-3">
              <Link
                to="/book"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[color:var(--am-pink)] text-navy text-[0.82rem] font-medium tracking-[0.2em] uppercase hover:bg-white transition-colors shadow-lg"
                data-testid="hero-book-button"
              >
                Book a Charter <ArrowRight size={14} />
              </Link>
              <Link
                to="/experiences"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-navy text-[0.82rem] font-medium tracking-[0.2em] uppercase hover:bg-[color:var(--am-pink)] transition-colors shadow-lg"
                data-testid="hero-experiences-button"
              >
                View Experiences
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-sand py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="overline mb-4">A Note from the Deck</div>
            <h2 className="font-serif-display text-4xl lg:text-5xl leading-tight text-navy">
              A 35ft Bavaria, a quiet breeze,<br />and a single anchor to lift.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-5 text-[color:var(--am-muted)] leading-relaxed">
            <p>
              Anchor Management is a privately-owned sailing charter based in
              Bermuda. No crowded decks, no rigid itineraries — just your group,
              the captain, and the rhythm of the Atlantic.
            </p>
            <p>
              Up to ten guests. Three cabins. Breakfast at anchor or cocktails at
              golden hour. Tell us how you'd like your day to feel, and we'll
              trim the sails accordingly.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-navy border-b border-navy pb-1 text-sm tracking-[0.2em] uppercase font-medium hover:gap-3 transition-all"
              data-testid="home-about-link"
            >
              About the boat <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* QUICK FEATURES */}
      <section className="bg-white py-16 border-y border-[color:var(--am-line)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4 gap-10">
          {[
            { icon: Users, label: "Capacity 10", sub: "up to 8 for hourly" },
            { icon: Bed, label: "3 Cabins", sub: "1 full bathroom" },
            { icon: LifeBuoy, label: "Safety First", sub: "life vests · noodles" },
            { icon: Wifi, label: "Wifi & TV", sub: "stay connected" },
          ].map((f, i) => (
            <div key={i} className="flex flex-col items-start gap-3" data-testid={`feature-${i}`}>
              <f.icon size={22} strokeWidth={1.4} className="text-navy" />
              <div>
                <div className="font-serif-display text-2xl text-navy leading-tight">{f.label}</div>
                <div className="text-xs tracking-[0.18em] uppercase text-[color:var(--am-muted)] mt-1">{f.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BERMUDA WATERS — promote the island */}
      <section className="relative bg-pink-soft" data-testid="bermuda-waters-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Why Bermuda's Waters</div>
            <h2 className="font-serif-display text-4xl lg:text-5xl text-navy leading-tight">
              The Atlantic's clearest <em className="italic">turquoise</em>.
            </h2>
            <p className="mt-6 text-navy/80 leading-relaxed">
              Visibility past 100 feet on a calm day. Pink-sand coves carved
              into the south shore. Mangrove inlets, hidden anchorages, and
              the warmest blues you'll ever see — all within an afternoon's
              sail. There's a reason the sailors who race here come back to live.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-3 gap-px bg-navy/10">
            <div className="bg-pink-soft p-6">
              <div className="font-serif-display text-4xl text-navy">68–84°F</div>
              <div className="overline mt-2">Sea temp · year-round</div>
            </div>
            <div className="bg-pink-soft p-6">
              <div className="font-serif-display text-4xl text-navy">100ft+</div>
              <div className="overline mt-2">Avg. visibility</div>
            </div>
            <div className="bg-pink-soft p-6">
              <div className="font-serif-display text-4xl text-navy">181</div>
              <div className="overline mt-2">Islands & islets</div>
            </div>
            <div className="bg-pink-soft p-6 col-span-3">
              <p className="italic text-navy/80 text-sm leading-relaxed">
                "The colour of the sea here is one nature didn't repeat anywhere else." — Mark Twain, after his Bermuda stays.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EXPERIENCES PREVIEW */}
      <section className="py-28 bg-sand">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="overline mb-4">Experiences</div>
              <h2 className="font-serif-display text-4xl lg:text-5xl text-navy leading-tight max-w-xl">
                Four ways to meet the sea.
              </h2>
            </div>
            <Link to="/experiences" className="text-sm tracking-[0.22em] uppercase font-medium text-navy border-b border-navy pb-1 w-fit hover:gap-3 flex items-center gap-2 transition-all" data-testid="home-experiences-all-link">
              View all <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {EXPERIENCES.map((exp, i) => (
              <Link
                key={exp.slug}
                to="/experiences"
                className="group relative block overflow-hidden img-zoom aspect-[4/5] md:aspect-[4/3]"
                data-testid={`home-experience-${exp.slug}`}
              >
                <img src={exp.image} alt={exp.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--am-navy)] via-[color:var(--am-navy)]/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-8 text-white">
                  <div className="overline text-white/80 mb-3">0{i + 1}</div>
                  <h3 className="font-serif-display text-3xl lg:text-4xl leading-tight">{exp.name}</h3>
                  <p className="text-sm text-white/80 mt-2">{exp.tagline}</p>
                  <div className="text-xs tracking-[0.2em] uppercase text-[color:var(--am-pink)] mt-4">
                    {exp.pricing}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MEMBER SPOTLIGHT (or empty-state Club CTA) */}
      <MemberSpotlight />

      {/* INSTAGRAM */}
      <InstagramStrip />

      {/* CTA BAND */}
      <section className="relative py-24 bg-pink-soft overflow-hidden">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
          <Anchor size={28} strokeWidth={1.4} className="text-navy mx-auto mb-6" />
          <h2 className="font-serif-display text-4xl lg:text-6xl text-navy leading-tight">
            Pick a date. We'll <em className="italic">unpack the rest.</em>
          </h2>
          <p className="text-[color:var(--am-muted)] mt-6 max-w-xl mx-auto">
            From $300/hr for up to 8 guests. Overnights from $1,500.
            Menus, music and itinerary, shaped to your taste.
          </p>
          <Link to="/book" className="btn-primary mt-10" data-testid="home-cta-book-button">
            Begin Booking <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
